import React, { useEffect, useState } from "react";
import { ActivityCard, ChoiceButton, ProgressPill, useShuffledChoices } from "../shared";

/**
 * ScenarioPick — "Pick the Pronoun" situation rounds.
 *
 * English one-line scenarios; choose the right pronoun from four (shuffled
 * per round so the authored correct_index position never shows). Completes
 * at data.pass_count or better; unlimited retries.
 */
export default function ScenarioPick({ data, onComplete, audio }) {
  const [roundIdx, setRoundIdx] = useState(0);
  const [attempt, setAttempt] = useState(0); // bumps each retry → fresh shuffle
  const [picked, setPicked] = useState(null); // choice index this round
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  const rounds = data.rounds;
  const round = rounds[roundIdx];
  const passed = correctCount >= data.pass_count;
  const view = useShuffledChoices(round.choices, round.correct_index, [round, attempt]);

  useEffect(() => {
    if (finished && passed) onComplete();
  }, [finished, passed, onComplete]);

  const handlePick = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    if (idx === view.correctIndex) setCorrectCount((n) => n + 1);
    audio.play(round.choices[round.correct_index]);
  };

  const next = () => {
    setPicked(null);
    if (roundIdx + 1 >= rounds.length) setFinished(true);
    else setRoundIdx((i) => i + 1);
  };

  const retry = () => {
    setRoundIdx(0);
    setAttempt((n) => n + 1);
    setPicked(null);
    setCorrectCount(0);
    setFinished(false);
  };

  if (finished) {
    return (
      <ActivityCard>
        <div className="text-center py-6">
          <p className="text-4xl m-0" aria-hidden="true">{passed ? "🏆" : "💪"}</p>
          <p className="font-cormorant font-semibold text-2xl mt-3 mb-1" style={{ color: "var(--parchment)" }}>
            {correctCount} / {rounds.length}
          </p>
          <p className="text-sm mb-4" style={{ color: "var(--parchment-dim)" }}>
            {passed
              ? data.pass_message || "Passed! You can pick the right pronoun for the situation."
              : `You need ${data.pass_count}/${rounds.length} to move on — give it another go.`}
          </p>
          <button
            type="button"
            onClick={retry}
            className="inline-flex items-center justify-center px-4 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={passed
              ? { border: "1px solid var(--panel-line)", color: "var(--parchment)", background: "transparent" }
              : { background: "var(--gold)", color: "var(--ink)" }}
          >
            {passed ? "Play again" : "Try again"}
          </button>
        </div>
      </ActivityCard>
    );
  }

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-5">
        <ProgressPill>Round {roundIdx + 1}/{rounds.length}</ProgressPill>
        <span className="text-sm" style={{ color: "var(--parchment-dim)" }}>
          {correctCount} correct · need {data.pass_count}
        </span>
      </div>

      <p className="text-base sm:text-lg text-center font-medium mb-6" style={{ color: "var(--parchment)" }}>
        {round.scenario_en}
      </p>

      <div className="grid grid-cols-2 gap-2.5 max-w-md mx-auto">
        {view.choices.map((choice, idx) => {
          let state = "idle";
          if (picked !== null) {
            if (idx === view.correctIndex) state = "correct";
            else if (idx === picked) state = "wrong";
            else state = "dim";
          }
          return (
            <ChoiceButton key={idx} arabic state={state} disabled={picked !== null} onClick={() => handlePick(idx)}>
              {choice}
            </ChoiceButton>
          );
        })}
      </div>

      {picked !== null && (
        <div className="flex justify-center mt-5">
          <button
            type="button"
            onClick={next}
            className="inline-flex items-center justify-center px-5 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            {roundIdx + 1 >= rounds.length ? "See results" : "Next round"}
          </button>
        </div>
      )}
    </ActivityCard>
  );
}
